import { useState } from 'react'
import type { JSX } from 'react'

import { Button, Heading, PlusIcon, Tooltip, toast } from '@buffer-mono/popcorn'

import type { PersonalAccessToken } from '../../hooks'
import { CliPromoCard } from '../CliPromoCard'
import { ConnectAgentCard } from '../ConnectAgentCard'
import { GenerateApiKeyCard } from '../GenerateApiKeyCard'
import { GenerateTokenDialog } from '../GenerateTokenDialog'
import { RevokeTokenDialog } from '../RevokeTokenDialog'
import { TokenInfo } from '../TokenInfo'

import styles from './PersonalTokenSection.module.css'

type GenerateTokenInput = {
  name?: string
  expiresIn?: number
  scopes?: string[]
}

type GenerateTokenResult =
  | { success: true; token: PersonalAccessToken }
  | { success: false; error: string }

type RevokeTokenResult = { success: true } | { success: false; error: string }

type DialogState =
  | { type: 'create' }
  | { type: 'revoke'; token: PersonalAccessToken }
  | { type: 'regenerate'; token: PersonalAccessToken }
  | null

interface PersonalTokenSectionProps {
  tokens: PersonalAccessToken[]
  maxTokens: number
  canUpgradeForMore: boolean
  organizationId: string | null
  generateToken: (input?: GenerateTokenInput) => Promise<GenerateTokenResult>
  revokeToken: (accessToken: string) => Promise<RevokeTokenResult>
  isGenerating: boolean
  isRevoking: boolean
  onTokenListRefresh: () => Promise<void>
  onUpgrade: () => void
  showOnboarding?: boolean
  onDismissOnboarding?: () => void
}

export const PersonalTokenSection = ({
  tokens,
  maxTokens,
  canUpgradeForMore,
  organizationId,
  generateToken,
  revokeToken,
  isGenerating,
  isRevoking,
  onTokenListRefresh,
  onUpgrade,
  showOnboarding,
  onDismissOnboarding,
}: PersonalTokenSectionProps): JSX.Element => {
  const [dialog, setDialog] = useState<DialogState>(null)
  const canCreateNewToken = tokens.length < maxTokens

  const refreshTokenList = async (): Promise<void> => {
    try {
      await onTokenListRefresh()
    } catch {
      toast.error('Unable to refresh key list. Please refresh the page.')
    }
  }

  const handleRevokeConfirm = async (): Promise<void> => {
    if (dialog?.type !== 'revoke' || isRevoking) {
      return
    }

    const result = await revokeToken(dialog.token.accessToken)

    if (result.success) {
      setDialog(null)
      toast.success('API key has been revoked.')
      await refreshTokenList()
    } else {
      toast.error('Unable to revoke key. Please try again later.')
    }
  }

  return (
    <>
      <div className={styles.container}>
        {tokens.length > 0 ? (
          <>
            <div className={styles.header}>
              <Heading as="h2" size="sm">
                Keys
              </Heading>

              {canCreateNewToken ? (
                <Button
                  variant="secondary"
                  onClick={(): void => setDialog({ type: 'create' })}
                >
                  <PlusIcon /> New Key
                </Button>
              ) : canUpgradeForMore ? (
                <Button variant="secondary" onClick={onUpgrade}>
                  <PlusIcon /> New Key
                </Button>
              ) : (
                <Tooltip
                  content={`Maximum of ${maxTokens} API keys reached`}
                  side="right"
                >
                  <span>
                    <Button variant="secondary" disabled>
                      <PlusIcon /> New Key
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>

            {tokens.map((token, index) => (
              <TokenInfo
                key={token.accessToken}
                token={token}
                organizationId={organizationId}
                onRevoke={(): void => setDialog({ type: 'revoke', token })}
                onRegenerate={(): void =>
                  setDialog({ type: 'regenerate', token })
                }
                showOnboarding={index === 0 ? showOnboarding : false}
                onDismissOnboarding={
                  index === 0 ? onDismissOnboarding : undefined
                }
              />
            ))}
          </>
        ) : (
          <div className={styles.emptyState}>
            <GenerateApiKeyCard
              onGenerate={(): void => setDialog({ type: 'create' })}
            />

            <div className={styles.cardRow}>
              <ConnectAgentCard />
              <CliPromoCard />
            </div>
          </div>
        )}
      </div>

      <RevokeTokenDialog
        open={dialog?.type === 'revoke'}
        onOpenChange={(open): void => {
          if (!open) {
            setDialog(null)
          }
        }}
        onConfirm={(): void => {
          void handleRevokeConfirm()
        }}
      />

      {dialog?.type === 'regenerate' && (
        <GenerateTokenDialog
          key={dialog.token.accessToken}
          mode="regenerate"
          open
          onOpenChange={(open): void => {
            if (!open) {
              setDialog(null)
            }
          }}
          currentToken={dialog.token.accessToken}
          currentKeyName={dialog.token.name ?? undefined}
          onRevoke={revokeToken}
          onGenerate={generateToken}
          onSuccess={(): void => {
            void refreshTokenList()
          }}
          isLoading={isRevoking || isGenerating}
        />
      )}

      {dialog?.type === 'create' && (
        <GenerateTokenDialog
          open
          onOpenChange={(open): void => {
            if (!open) {
              setDialog(null)
            }
          }}
          onGenerate={generateToken}
          onSuccess={(): void => {
            void refreshTokenList()
          }}
          isLoading={isGenerating}
        />
      )}
    </>
  )
}
